import { ReactElement, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/esm/Spinner';
import { useTranslation } from 'react-i18next';
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params';
import { CommitteeAPI, JurisdictionAPI } from '../../../api';
import DayPickerInputDropdown from '../../Shared/Selectors/DayPickerInputDropdown';
import CommitteeDropdown from './CommitteeDropdown';
import WorkspaceFilter from '../../Shared/Search/MultiSelectFilter';
import { OptionType } from '../../Shared/DropDowns/SingleSelectDropDown';
import CustomDropdown from '../../Shared/DropDowns/CustomDropdown';
import DropDownButton from '../../Shared/Buttons/DropDownButton';
import MultiSelectForm from '../../Shared/Forms/MultiSelectForm';
import { getSessionOptions } from '../../../helpers/BillSearchHelper';
import CloseIcon from '../../../components/svg/CloseIcon';
import { trackHearingEventsFiltered } from '../../../analytics/HearingsAnalytics';
import { useSelector } from 'react-redux';
import { State } from '../../../dux/@types';
import MultipleSelectDropDown from '../../Shared/DropDowns/MultipleSelectDropDown';
import { getLegislaturesOptions } from '../../../helpers/JurisdictionSelectionHelper';
import { CURRENT_SESSION_FILTER } from '../../../api/jurisdiction';

type JurisdictionHearingsFiltersProps = {
  query: DecodedValueMap<QueryParamConfigMap>;
  resetScroll: () => void;
  setQuery: SetQuery<QueryParamConfigMap>;
  jurisAbbr: string;
  workspaces: OptionType<number>[];
};

const JurisdictionHearingsFilters = (
  props: JurisdictionHearingsFiltersProps,
): ReactElement => {
  const { query, resetScroll, setQuery, jurisAbbr: jurisAbbr, workspaces } = props;
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [showFromDatePicker, setShowFromDatePicker] = useState(false);
  const [showToDatePicker, setShowToDatePicker] = useState(false);
  const [selectedCommittees, setSelectedCommittees] = useState<string[]>(
    query?.cmteNames ? [query.cmteNames] : [],
  );
  const [selectedSessions, setSelectedSessions] = useState<string[]>(
    query?.sessions.filter((s: string | null) => s !== null),
  );
  const defaultJurisdictions = jurisAbbr
    ? [jurisAbbr]
    : query?.jurisdictions.filter((s: string | null) => s !== null);
  const [selectedJurisdictions, setSelectedJurisdictions] =
    useState<string[]>(defaultJurisdictions);
  const [workspaceFilterMode, setWorkspaceFilterMode] = useState<string | undefined>(
    query?.workspaceFilterMode,
  );
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>(
    query?.workspaces ?? [],
  );
  const [searchText, setSearchText] = useState('');
  const [showSessionFilter, setShowSessionFilter] = useState(false);

  const showSingleJurisdiction = !!jurisAbbr;

  const { t } = useTranslation();

  const {
    data: committees,
    isFetching: isFetchingCommittees,
    refetch: refetchCommittees,
  } = CommitteeAPI.endpoints.getCommitteesByJurisdiction.useQuery(
    selectedJurisdictions,
  );

  const { data: jurisdictionSessions, refetch: refetchSessions } =
    JurisdictionAPI.endpoints.getSessions.useQuery(selectedJurisdictions);

  const orgUserEmail = useSelector(
    (state: State) => state.account.organizationUser?.email,
  );

  const uniqueCommittees = useMemo(() => {
    if (!committees) return [];

    const seenNames = new Set();

    return committees.filter((v) => {
      if (seenNames.has(v.name)) {
        return false;
      } else {
        seenNames.add(v.name);
        return true;
      }
    });
  }, [committees]);

  useEffect(() => {
    if (query.startDateRange) {
      setFromDate(query.startDateRange);
    }
    if (query.endDateRange) {
      setToDate(query.endDateRange);
    }
  }, [query]);

  useEffect(() => {
    setSelectedCommittees([]);
    setSelectedSessions([CURRENT_SESSION_FILTER]);
    setSearchText('');

    refetchCommittees();
    refetchSessions();
  }, [selectedJurisdictions]);

  const jurKey = useMemo(
    () => selectedJurisdictions.join('|'),
    [selectedJurisdictions],
  );

  const getCommitteeNames = () => {
    let cmtes = undefined;
    if (searchText && !selectedCommittees.length) {
      cmtes = searchText;
    } else if (searchText) {
      cmtes = searchText + '+' + selectedCommittees.join('+');
    } else {
      cmtes = selectedCommittees.join('+');
    }

    return cmtes;
  };

  const supportedJurisdictions = useSelector(
    (state: State) => state.account.supportedJurisdictions,
  );

  const jurisdictionOptions = getLegislaturesOptions(supportedJurisdictions || []);

  // Move "US Congress" to the top of dropdwon options
  const index = jurisdictionOptions.findIndex((jurOption) => jurOption.value === 'usa');
  if (index !== -1)
    jurisdictionOptions.unshift(...jurisdictionOptions.splice(index, 1));

  const handleSearch = (): void => {
    const cmteNames = getCommitteeNames();

    setQuery({
      startDateRange: fromDate,
      endDateRange: toDate,
      sessions: selectedSessions,
      workspaces: selectedWorkspaces,
      workspaceFilterMode,
      cmteNames,
      jurisdictions: selectedJurisdictions,
    });
    setSearchText('');
    resetScroll();
    trackHearingEventsFiltered(
      cmteNames.split('+'),
      selectedSessions,
      fromDate?.toDateString(),
      toDate?.toDateString(),
      orgUserEmail,
      selectedWorkspaces,
      workspaceFilterMode,
    );
  };

  const clearAll = (): void => {
    setSelectedCommittees([]);
    setSelectedSessions([]);
    setToDate(undefined);
    setFromDate(undefined);
    setSelectedWorkspaces([]);
    setWorkspaceFilterMode(undefined);
    setSelectedJurisdictions(!!jurisAbbr ? [jurisAbbr] : []);
    setSearchText('');
    resetScroll();

    setQuery({
      startDateRange: undefined,
      endDateRange: undefined,
      cmteNames: undefined,
      sessions: undefined,
      workspaces: undefined,
      workspaceFilterMode: undefined,
      jurisdictions: undefined,
    });
  };

  const fromDateLabel = fromDate ? fromDate.toLocaleDateString() : 'Start';
  const toDateLabel = toDate ? toDate.toLocaleDateString() : 'End';
  const maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 2));

  const sessionOptions = jurisdictionSessions
    ? getSessionOptions(jurisdictionSessions, !showSingleJurisdiction ? true : false)
    : getSessionOptions([]);

  const renderMultiSelectOptions = (
    currentSelection: string[] | undefined,
    options: OptionType<string>[],
    updateSelection: (values: string[]) => void,
  ): ReactElement => {
    return (
      <MultiSelectForm
        currentSelection={currentSelection ?? []}
        options={options}
        showClearAll
        showSelectAll
        showSelectButtonsBottom
        singleColumn
        updateSelection={updateSelection}
      />
    );
  };

  return (
    <div className="filters jurisdiction-hearings-filter container-fluid mx-3 mb-2 mt-0">
      <div className="row">
        {isFetchingCommittees && (
          <Spinner animation="border" className="mx-22 mt-2" role="status" />
        )}
        {!showSingleJurisdiction && (
          <div className="filter jurisdictions mr-2 mt-2">
            <MultipleSelectDropDown
              currentSelectedValues={selectedJurisdictions}
              floatSelectedToTop
              onChange={(selectedFilterValues) => {
                setSelectedJurisdictions([...selectedFilterValues]);
              }}
              options={jurisdictionOptions}
              title={`Jurisdictions`}
            />
          </div>
        )}
        <div className="adv-filter mr-2 mt-2">
          <CustomDropdown
            setShowDropdown={setShowSessionFilter}
            showDropdown={showSessionFilter}
            target={
              <DropDownButton
                disabled={false}
                dropdownDisplayed={showSessionFilter}
                id={'hearings-session-filter-btn'}
                label={t('buttons.search.sessions')}
                onClick={() => setShowSessionFilter(!showSessionFilter)}
                selectedCount={selectedSessions.length || undefined}
              />
            }
          >
            <div className="adv-search-selector">
              {renderMultiSelectOptions(
                selectedSessions,
                sessionOptions,
                setSelectedSessions,
              )}
            </div>
          </CustomDropdown>
        </div>
        <CommitteeDropdown
          // Hack to force component rerender when selectedJurisdiction changes. Necessary to render updated available committees
          availableCommittees={uniqueCommittees}
          isSingleJurisdiction={showSingleJurisdiction}
          key={`${jurKey}:${uniqueCommittees.length}`}
          searchText={searchText}
          selectedCommittees={selectedCommittees}
          setSearchText={setSearchText}
          setSelectedCommittees={setSelectedCommittees}
        />
        <WorkspaceFilter
          className="workspaces ml-2 mt-2"
          options={workspaces}
          selectedFilterOption={workspaceFilterMode}
          selectedValues={selectedWorkspaces}
          setSelectedFilterOption={setWorkspaceFilterMode}
          setSelectedValues={setSelectedWorkspaces}
          title="Workspaces"
        />
        <div className="row date-pickers ml-2 mt-2">
          <DayPickerInputDropdown
            label={fromDateLabel}
            maxDate={toDate ?? maxDate}
            selectedDate={fromDate}
            setSelectedDate={setFromDate}
            setShowDropdown={setShowFromDatePicker}
            showDropdown={showFromDatePicker}
          />
          <span className="divider" />
          <DayPickerInputDropdown
            label={toDateLabel}
            maxDate={maxDate}
            minDate={fromDate}
            selectedDate={toDate}
            setSelectedDate={setToDate}
            setShowDropdown={setShowToDatePicker}
            showDropdown={showToDatePicker}
          />
        </div>
        <div className="d-inline-flex ml-2 default-search-buttons g-2 mt-2">
          <button
            className="btn btn-sm btn-outline-secondary float-right btn-teal"
            id="staffer-data-search-button"
            onClick={handleSearch}
            type="submit"
          >
            {t('buttons.search.submitButtonText')}
          </button>
          <button
            className="btn btn-link text-danger p-0 d-flex align-items-center ml-3"
            id="clear-jurisdiction-filter-btn"
            onClick={clearAll}
            type="button"
          >
            <CloseIcon fill="#EB5757" />
            <span className="mx-1">Clear All</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JurisdictionHearingsFilters;
